import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Wrench, Settings, CheckCircle, Shield, Zap, Cog, Layers } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import undercarriage from '../../assets/Images/services/undercarriage.webp'

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.2,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

const UnderCarriageRefurbishment = () => {
  const navigate = useNavigate();

  const keyPoints = [
    'Complete overhaul and rebuilding of undercarriage components',
    'Track chain, rollers, idlers, sprockets restored or replaced',
    'Precision alignment for smooth and stable machine movement',
    'High‑strength wear‑resistant parts used for long durability',
    'Enhanced track performance and reduced fuel consumption',
  ];

  const processSteps = [
    {
      icon: <Wrench className="text-blue-600" size={24} />,
      title: 'Inspection & Measurement',
      description: 'Complete undercarriage measurement and identification of worn components.',
    },
    {
      icon: <Layers className="text-blue-600" size={24} />,
      title: 'Track Chain & Shoes Refurbishing',
      description: 'Track chain repairs, bushing rotation, and shoe replacement as required.',
    },
    {
      icon: <Cog className="text-blue-600" size={24} />,
      title: 'Rollers & Idlers Restoration',
      description: 'Rebuilding worn rollers and idlers for smooth movement.',
    },
    {
      icon: <Shield className="text-blue-600" size={24} />,
      title: 'Sprocket Replacement',
      description: 'Installation of new or refurbished sprockets ensuring proper track engagement.',
    },
    {
      icon: <Settings className="text-blue-600" size={24} />,
      title: 'Alignment & Tensioning',
      description: 'Proper track tensioning and alignment for extended component life.',
    },
    {
      icon: <CheckCircle className="text-blue-600" size={24} />,
      title: 'Final Testing & Quality Check',
      description: 'Machine is tested under load to ensure perfect undercarriage functioning.',
    },
  ];

  const objectives = [
    'Restore undercarriage performance to optimal OEM level',
    'Minimize machine downtime and operational cost',
    'Increase stability, traction, and fuel efficiency',
    'Ensure long-lasting durability with top‑grade components',
    'Provide cost‑effective solutions over complete replacement',
  ];

  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) controls.start('show');
  }, [controls, inView]);

  return (
    <div className="text-gray-800 leading-relaxed max-w-full overflow-x-hidden pt-20 md:pt-24">
      <Header />

      {/* Hero Section */}
      <motion.section initial="hidden" animate="show" variants={container} className="relative bg-gray-50 py-16 md:py-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">

            {/* Left */}
            <motion.div variants={item} className="md:w-1/2 mb-10 md:mb-0 md:pr-10">
              <motion.h1 variants={fadeInUp} className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00325b] mb-6">
                Under Carriage Refurbishment
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-lg sm:text-xl text-gray-700 mb-8">
                Professional undercarriage restoration that enhances stability, traction, and machine life.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-all">
                  Get a Free Quote
                </motion.button>

                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }} onClick={() => navigate('/contact')} className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium py-3 px-6 rounded-lg transition-all flex items-center gap-2">
                  <Shield size={20} /> Service Enquiry
                </motion.button>
              </motion.div>
            </motion.div>

            {/* Image */}
            <motion.div variants={item} className="md:w-1/2 relative">
              <motion.img
                src={undercarriage}
                alt="Under Carriage Refurbishment"
                className="w-full rounded-xl shadow-2xl"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Description Section */}
      <motion.section ref={ref} initial="hidden" animate={controls} variants={container} className="py-20 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="container mx-auto px-4">
          <motion.div variants={container} className="max-w-5xl mx-auto text-center mb-16">
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Reliable <span className="text-blue-600">Undercarriage Restoration</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-gray-700 max-w-3xl mx-auto">
              Using industry‑grade precision tools and genuine parts, we restore your machine’s undercarriage to factory-level performance.
            </motion.p>
          </motion.div>

          <motion.div variants={container} className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Why Choose Us */}
            <motion.div variants={item} className="bg-white p-8 rounded-2xl shadow-lg border hover:shadow-xl transition-all">
              <div className="bg-blue-50 w-14 h-14 flex items-center justify-center rounded-xl mb-6">
                <Zap className="text-blue-600" size={28} />
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-[#00325b]">Why Choose Us</h3>
              <ul className="space-y-4">
                {keyPoints.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="text-green-600" size={18} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Objectives */}
            <motion.div variants={item} className="bg-white p-8 rounded-2xl shadow-lg border hover:shadow-xl transition-all">
              <div className="bg-blue-50 w-14 h-14 flex items-center justify-center rounded-xl mb-6">
                <Settings className="text-blue-600" size={28} />
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-[#00325b]">Our Objectives</h3>
              <ul className="space-y-4">
                {objectives.map((obj, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="text-green-600" size={18} />
                    <span>{obj}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Process Section */}
      {/* <motion.section initial="hidden" whileInView="show" viewport={{ once: true }} variants={container} className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <motion.h2 variants={fadeInUp} className="text-3xl font-bold text-[#00325b] mb-4">
              Refurbishment Process
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-600 max-w-3xl mx-auto">
              A structured process ensuring maximum undercarriage efficiency.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, idx) => (
              <motion.div key={idx} variants={item} className="p-6 bg-gray-50 rounded-xl shadow border hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  {step.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section> */}

      {/* CTA Section */}
      <section id="contact" className="bg-blue-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Undercarriage Refurbishment?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Contact us today for professional and reliable undercarriage refurbishment services.</p>
          <button onClick={() => navigate('/contact')} className="bg-white text-blue-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-all">
            Request Service
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UnderCarriageRefurbishment;
